/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonMenuButton,
  IonModal,
  IonPage,
  IonPopover,
  IonTitle,
  IonToolbar,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from "@ionic/react";
import { format, isSameDay } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, withRouter } from "react-router";
import { useRecoilValueLoadable, useSetRecoilState } from "recoil";
import {
  Button_Cancel,
  Button_Checkbox_Active,
  Button_Checkbox_Inactive,
  Button_Download_White,
  Button_Filter,
  Button_Filter_Active,
  Button_Menu,
  Button_Search,
  Button_Search_Active,
  Button_Tickbox_3_Inactive,
  Icon_Search_Small,
  Image_Timeline_Empty,
} from "@assets/images";
import TimelineContainer from "@components/timeline/TimelineContainer";
import * as FirebaseService from "@controllers/FirebaseService";
import {
  IEvidence,
  INewTimelineEvidence,
  IParapassBooksProgress,
  IParapassQuizData,
  IParapassUsageData,
  IPlusUsageData,
  IUser,
  ParapassPDFRequestParams,
} from "@interfaces/Interfaces";
import {
  evidenceAtom,
  parapassBooksProgressAtom,
  parapassQuizDataAtom,
  parapassUsageDataAtom,
  plusDataAtom,
  userAtom,
} from "@state/State";
import _ from "lodash";
import DataController from "@controllers/DataController";
import "./Timeline.css";
import { EventRegister } from "react-native-event-listeners";
import HeaderTitle from "@components/common/HeaderTitle";
import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardInfo } from "@capacitor/keyboard";
import * as DatabaseService from "@controllers/DatabaseService";
import { blobToBase64 } from "@utils/parapassUtils";
import { useConnectionStatus } from "@hooks/app/useConnectionStatus";
import { useLearningLoop } from "@hooks/programs/useLearningLoop";
import { parapassTimelineDataAtom } from "@state/TimelineState";
import { FileOpener } from "@capacitor-community/file-opener";
import { Directory } from "@capacitor/filesystem";
import WriteBlob from "capacitor-blob-writer";
import { saveAs } from "file-saver";
import * as TimelineUtils from "@utils/timelineUtils";
import TimelineItem from "@components/timeline/TimelineItem";
import { TimelineData } from "@interfaces/TimelineTypes";

const Timeline: React.FC = () => {
  const history = useHistory();

  const connectionStatus = useConnectionStatus();
  const { hasLearningLoop } = useLearningLoop();
  const setParapassTimelineData = useSetRecoilState(parapassTimelineDataAtom);

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const evidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const plusData = useRecoilValueLoadable<IPlusUsageData[] | null>(plusDataAtom);
  const parapassQuizData = useRecoilValueLoadable<IParapassQuizData[] | null>(parapassQuizDataAtom);
  const parapassUsageData = useRecoilValueLoadable<IParapassUsageData[] | null>(parapassUsageDataAtom);
  const parapassBooksProgress = useRecoilValueLoadable<IParapassBooksProgress[] | null>(parapassBooksProgressAtom);

  const searchRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLIonModalElement>(null);
  const popoverRef = useRef<HTMLIonPopoverElement>(null);

  const [sortedPlusData, setSortedPlusData] = useState<IPlusUsageData[]>([]);
  const [sortedParapassData, setSortedParapassData] = useState<IParapassQuizData[]>([]);
  const [sortedParapassUsageData, setSortedParapassUsageData] = useState<IParapassUsageData[]>([]);
  const [sortedParapassBooksProgress, setSortedParapassBooksProgress] = useState<IParapassBooksProgress[]>([]);

  const [searchVisible, setSearchVisible] = useState<boolean>(false);
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(format(new Date(), "MMM yyyy"));
  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [sortedUsageData, setSortedUsageData] = useState<TimelineData[]>([]);
  const [searchResults, setSearchResults] = useState<TimelineData[]>([]);

  const [plusFilters, setPlusFilters] = useState<string[]>([]);
  const [parapassFilters, setParapassFilters] = useState<string[]>([]);

  const [allFilterSelected, setAllFilterSelected] = useState<boolean>(true);
  const [allPlusSelected, setAllPlusSelected] = useState<boolean>(false);
  const [allParapassSelected, setAllParapassSelected] = useState<boolean>(false);

  const [selectedPlusFilters, setSelectedPlusFilters] = useState<any[]>([]);
  const [selectedParapassFilters, setSelectedParapassFilters] = useState<any[]>([]);

  const [popoverFilter, setPopoverFilter] = useState<boolean>(false);

  const [keyboardActive, setKeyboardActive] = useState<boolean>(false);
  const [keyboardHeight, setKeyboardHeight] = useState<number>(0);

  useEffect(() => {
    const windowResized = () => {
      setPopoverVisible(false);
      setFilterVisible(false);

      if (window.innerWidth >= 600) {
        setPopoverFilter(true);
      } else {
        setPopoverFilter(false);
      }
    };

    window.addEventListener("resize", windowResized);

    windowResized();

    return () => {
      window.removeEventListener("resize", windowResized);
    };
  }, []);

  useIonViewDidEnter(() => {
    EventRegister.emit("tab-bar/visibility-changed", true);
  });

  useIonViewDidLeave(() => {
    closeSearch();
  });

  useEffect(() => {
    const logSearch = async (): Promise<void> => {
      await FirebaseService.logEvent("evidence_search_pressed", {});
    };

    searchVisible && logSearch();
  }, [searchVisible]);

  useEffect(() => {
    const sortData = (): void => {
      let plusCopy = _.cloneDeep(plusData.contents) ?? [];
      let parapassCopy = _.cloneDeep(parapassQuizData.contents) ?? [];
      let parapassUsageCopy = _.cloneDeep(parapassUsageData.contents) ?? [];
      let parapassBooksProgressCopy = _.cloneDeep(parapassBooksProgress.contents) ?? [];

      plusCopy = plusCopy.map((item: IPlusUsageData) => ({
        ...item,
        filterType: DataController.getJRCALCGuidelineType(item.GLID),
        sortableDate: new Date(item.timestamp),
      }));
      parapassCopy = parapassCopy.map((item: IParapassQuizData) => ({
        ...item,
        filterType: DataController.getParapassQuizType(item.type),
        sortableDate: new Date(item.date),
      }));
      parapassUsageCopy = parapassUsageCopy.map((item: IParapassUsageData) => ({
        ...item,
        filterType: item.ContentType,
        sortableDate: new Date(item.TimeStamp),
      }));
      parapassBooksProgressCopy = parapassBooksProgressCopy.map((item: IParapassBooksProgress) => ({
        ...item,
        filterType: item.type,
        sortableDate: new Date(item.time),
      }));

      const uniquePlus: IPlusUsageData[] = [];

      for (let i = 0; i < plusCopy.length; i++) {
        const item: IPlusUsageData = plusCopy[i];

        const included = uniquePlus.findIndex(
          (value) => value.GLID === item.GLID && isSameDay(new Date(item.timestamp), new Date(value.timestamp))
        );

        if (included > -1) {
          const object = uniquePlus[included];
          const count = object.count ?? 1;
          uniquePlus.splice(included, 1, { ...object, count: count + 1 });
        } else {
          uniquePlus.push(item);
        }
      }

      const sorted = _.concat(uniquePlus, parapassCopy, parapassUsageCopy, parapassBooksProgressCopy);
      const hasParapassData =
        parapassCopy.length > 0 || parapassUsageCopy.length > 0 || parapassBooksProgressCopy.length > 0;

      const hasPlusData = plusCopy.length > 0;

      hasParapassData &&
        setParapassFilters(
          TimelineUtils.getParapassFilters({
            parapassQuizData: parapassCopy,
            parapassUsageData: parapassUsageCopy,
            parapassBooksProgress: parapassBooksProgressCopy,
          })
        );
      hasPlusData && setPlusFilters(TimelineUtils.fetPlusFilters({ plusData: plusCopy }));
      setSortedPlusData(_.orderBy(plusCopy, ["timestamp"], ["desc"]));
      setSortedParapassData(_.orderBy(parapassCopy, ["date"], ["desc"]));
      setSortedParapassBooksProgress(_.orderBy(parapassBooksProgressCopy, ["time"], ["desc"]));
      setSortedUsageData(_.orderBy(sorted, ["sortableDate"], ["desc"]));
      setSortedParapassUsageData(_.orderBy(parapassUsageCopy, ["TimeStamp"], ["desc"]));
      setSearchResults(_.orderBy(sorted, ["sortableDate"], ["desc"]));
    };

    plusData.state === "hasValue" && parapassQuizData.state === "hasValue" && sortData();
  }, [parapassQuizData.contents, parapassQuizData.state, plusData.contents, plusData.state, user]);

  useEffect(() => {
    const setPageName = async (): Promise<void> => {
      await FirebaseService.setScreenName("timeline");
    };

    setPageName();
  }, []);

  useEffect(() => {
    if (Capacitor.getPlatform() === "ios") {
      Keyboard.addListener("keyboardWillShow", (info: KeyboardInfo) => {
        setKeyboardActive(true);
        setKeyboardHeight(info.keyboardHeight);
      });

      Keyboard.addListener("keyboardWillHide", () => {
        setKeyboardActive(false);
      });
    }
  }, []);

  const closeSearch = useCallback((): void => {
    setSearchText("");
    setSearchVisible(false);
  }, []);

  const toggleFilterAll = useCallback((): void => {
    if (!allFilterSelected) {
      setAllFilterSelected(true);
      setAllPlusSelected(false);
      setAllParapassSelected(false);
      setSelectedPlusFilters([]);
      setSelectedParapassFilters([]);
    }
  }, [allFilterSelected]);

  useEffect(() => {
    if (allPlusSelected && allParapassSelected) {
      toggleFilterAll();
    }
  }, [allParapassSelected, allPlusSelected, toggleFilterAll]);

  useEffect(() => {
    if (
      !allFilterSelected &&
      !allPlusSelected &&
      !allParapassSelected &&
      selectedParapassFilters.length === 0 &&
      selectedPlusFilters.length === 0
    ) {
      toggleFilterAll();
    }
  }, [
    allFilterSelected,
    allParapassSelected,
    allPlusSelected,
    selectedParapassFilters,
    selectedPlusFilters,
    toggleFilterAll,
  ]);

  useEffect(() => {
    if (searchVisible) {
      const searchPlaceholders: string[] = [
        format(new Date(), "MMM yyyy"),
        ...(sortedParapassData.length > 0 ? ["ParaPass"] : []),
        ...(sortedParapassUsageData.length > 0 ? ["ParaPass"] : []),
        ...(sortedParapassBooksProgress.length > 0 ? ["ParaPass"] : []),
        ...(sortedPlusData.length > 0 ? ["JRCALC"] : []),
        ...searchResults.map((item) => item.title),
      ];

      setSearchPlaceholder(searchPlaceholders[Math.floor(Math.random() * searchPlaceholders.length)]);

      try {
        setTimeout(() => {
          searchRef.current?.focus();
        }, 250);
      } catch (error) {
        console.log(error);
      }
    } else {
      closeSearch();
    }
  }, [closeSearch, searchResults, searchVisible, sortedParapassData.length, sortedPlusData.length]);

  useEffect(() => {
    if (searchText.length > 0) {
      const search = searchText.toLowerCase();
      let copy = _.cloneDeep(sortedUsageData) || [];

      copy = copy.filter((item) => {
        if ("quizSet" in item) {
          if (allFilterSelected || allParapassSelected) {
            return true;
          } else if (selectedParapassFilters.length > 0) {
            return selectedParapassFilters.includes(item.filterType);
          }
        } else if ("ContentType" in item) {
          if (allFilterSelected || allParapassSelected) {
            return true;
          } else if (selectedParapassFilters.length > 0) {
            return selectedParapassFilters.includes(item.filterType);
          }
        } else if (TimelineUtils.isParapassBooksProgress(item)) {
          if (allFilterSelected || allParapassSelected) {
            return true;
          } else if (selectedParapassFilters.length > 0) {
            return selectedParapassFilters.includes(TimelineUtils.mapParapassBookFilter(item.filterType));
          }
        } else if ("GLID" in item) {
          if (allFilterSelected || allPlusSelected) {
            return true;
          } else if (selectedPlusFilters.length > 0) {
            return selectedPlusFilters.includes(item.filterType);
          }
        }

        return false;
      });

      copy = _.filter(copy, (item) => {
        return (
          ("quizSet" in item && "ParaPass".toLowerCase().includes(search)) ||
          ("GLID" in item && "JRCALC".toLowerCase().includes(search)) ||
          ("ContentType" in item && "ParaPass".toLowerCase().includes(search)) ||
          (TimelineUtils.isParapassBooksProgress(item) && "ParaPass".toLowerCase().includes(search)) ||
          item.title.toLowerCase().includes(search) ||
          format(item.sortableDate!, "dd MMM yyyy").toLowerCase().includes(search)
        );
      });
      setSearchResults(copy);
    } else {
      let copy = _.cloneDeep(sortedUsageData) || [];

      if (allFilterSelected || (allPlusSelected && allParapassSelected)) {
        setSearchResults(copy);
      } else {
        copy = copy.filter((item) => {
          if ("quizSet" in item) {
            if (allParapassSelected) {
              return true;
            } else if (selectedParapassFilters.length > 0) {
              return selectedParapassFilters.includes(item.filterType);
            }
          } else if ("ContentType" in item) {
            if (allParapassSelected) {
              return true;
            } else if (selectedParapassFilters.length > 0) {
              return selectedParapassFilters.includes(item.filterType);
            }
          } else if (TimelineUtils.isParapassBooksProgress(item)) {
            if (allParapassSelected) {
              return true;
            } else if (selectedParapassFilters.length > 0) {
              return selectedParapassFilters.includes(TimelineUtils.mapParapassBookFilter(item.filterType));
            }
          } else if ("GLID" in item) {
            if (allPlusSelected) {
              return true;
            } else if (selectedPlusFilters.length > 0) {
              return selectedPlusFilters.includes(item.filterType);
            }
          }

          return false;
        });

        setSearchResults(copy);
      }
    }
  }, [
    allFilterSelected,
    allParapassSelected,
    allPlusSelected,
    searchText,
    selectedParapassFilters,
    selectedParapassFilters.length,
    selectedPlusFilters,
    selectedPlusFilters.length,
    sortedUsageData,
  ]);

  const openFilter = (): void => {
    if (window.innerWidth >= 600) {
      setPopoverVisible(true);
    } else {
      setFilterVisible(true);
    }
  };

  const toggleSearch = (): void => {
    setSearchVisible(!searchVisible);
  };

  const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchText(event.target.value);
  };

  const searchFieldButtonClicked = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Escape") {
      closeSearch();
    }
  };

  const clearSearch = (): void => {
    setSearchText("");
    searchRef.current?.focus();
  };

  const downloadTimelineData = async (): Promise<void> => {
    const csv = TimelineUtils.generateCSV(searchResults);

    const BOM = "\uFEFF";
    const csvWithBOM = BOM + csv;
    const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8" });

    if (await DataController.isWebAsync()) {
      saveAs(blob, `ParaFolio_Timeline_${format(new Date(), "dd-MM-yyyy")}.csv`);
    } else {
      const filePath = await WriteBlob({
        path: `ParaFolio/Exports/CSV/ParaFolio_Timeline_${format(new Date(), "dd-MM-yyyy")}.csv`,
        blob,
        directory: Directory.Library,
        recursive: true,
      });

      await FileOpener.open({
        filePath,
        contentType: "text/csv",
        openWithDefault: false,
      });
    }

    await FirebaseService.logEvent("timeline_csv_exported");
  };

  const readEvidence = (timelineReference: string): void => {
    if (evidence.state === "hasValue" && evidence.contents) {
      const found = evidence.contents.find((item) => item.timelineReference === timelineReference);
      const _evidence = _.pickBy(_.cloneDeep(found), _.identity);

      history.push(`/evidence/read/${_evidence.id}`);
    }
  };

  const addEvidence = async (item: TimelineData, timelineReference: string): Promise<void> => {
    let object: INewTimelineEvidence | object = {};

    if ("quizSet" in item) {
      if (hasLearningLoop) {
        setParapassTimelineData(item);
        EventRegister.emit("learning-loop/add-evidence-timeline", object);
      } else {
        // Parapass
        const totalScore = item.score.correct + item.score.incorrect + item.score.unanswered;

        const params: ParapassPDFRequestParams = {
          contactID: user.contents.userData.contactID,
          token: user.contents.token,
          date: format(new Date(item.date), "do MMMM yyyy"),
          quizName: item.title,
          correctCount: `${item.score.correct}`,
          questionCount: `${totalScore}`,
          userName: user.contents.userData.userName,
          quizType: item.type,
        };

        let pdfString;
        let pdf;

        try {
          if (connectionStatus?.connected) {
            pdf = await DatabaseService.getParapassCertificate(params);
            pdfString = await blobToBase64(pdf);
          }
        } catch (error) {
          console.error(error);
        }

        object = _.extend(object, {
          title: item.title,
          date: new Date(),
          activityDate: new Date(item.sortableDate!),
          activityDescription: `Completed ParaPass “${item.title}” quiz and scored ${item.score.correct}/${totalScore} on ${format(item.sortableDate!, "dd MMM yyyy")}`,
          timelineReference,
          pdfData: {
            pdf: pdfString,
            size: pdf?.size,
            certificateFileName: `ParaPass_Certificate_${format(new Date(item.date), "dd-MMM-yyyy_hh-mm-ss")}.pdf`,
          },
        });

        FirebaseService.logEvent("add_timeline_evidence_pressed", {
          type: "ParaPass",
          quizID: item.quizSet,
          title: item.title,
        });

        EventRegister.emit("evidence-modal/add-evidence-timeline", object);
      }
    } else if ("ContentType" in item) {
      // ParaPass Usage
      object = _.extend({
        title: item.title,
        date: new Date(),
        activityDate: new Date(item.sortableDate!),
        activityDescription: `Viewed ParaPass ${item.ContentType} “${item.title}” on ${format(item.sortableDate!, "dd MMM yyyy")}`,
        timelineReference,
      });

      FirebaseService.logEvent("add_timeline_evidence_pressed", {
        type: "ParaPass Usage",
        contentID: item.ContentID,
        title: item.title,
      });

      EventRegister.emit("evidence-modal/add-evidence-timeline", object);
    } else if (TimelineUtils.isParapassBooksProgress(item)) {
      // ParaPass Books Progress
      const bookItem = item as IParapassBooksProgress;
      let activityDescription;
      if (typeof bookItem.ChapterNumber === "number") {
        activityDescription = `Read ParaPass Book “${bookItem.title}” Chapter ${bookItem.ChapterNumber} - “${bookItem.ChapterName}” on ${format(bookItem.sortableDate!, "dd MMM yyyy")}`;
      } else {
        activityDescription = `Read ParaPass Book “${bookItem.title}” on ${format(bookItem.sortableDate!, "dd MMM yyyy")}`;
      }

      object = _.extend({
        title: item.title,
        date: new Date(),
        activityDate: new Date(item.sortableDate!),
        activityDescription,
        timelineReference,
      });

      FirebaseService.logEvent("add_timeline_evidence_pressed", {
        type: "ParaPass Books Progress",
        title: item.title,
        ...(bookItem.ChapterNumber && { chapterNumber: bookItem.ChapterNumber }),
        ...(bookItem.ChapterName && { chapterName: bookItem.ChapterName }),
        date: format(bookItem.sortableDate!, "dd MMM yyyy"),
      });

      EventRegister.emit("evidence-modal/add-evidence-timeline", object);
    } else if ("GLID" in item) {
      // JRCALC
      object = _.extend({
        title: item.title,
        date: new Date(),
        activityDate: new Date(item.sortableDate!),
        activityDescription: `Viewed JRCALC ${DataController.getJRCALCGuidelineType(item.GLID)} “${item.title}” ${item.count ? `${item.count} time${item.count === 1 ? "" : "s"}` : ""} ${format(item.sortableDate!, "dd MMM yyyy")}`,
        timelineReference,
      });

      FirebaseService.logEvent("add_timeline_evidence_pressed", {
        type: "JRCALC",
        GLID: item.GLID,
        title: item.title,
      });

      EventRegister.emit("evidence-modal/add-evidence-timeline", object);
    }
  };

  const togglePlusFilter = (value: string): void => {
    let array = _.clone(selectedPlusFilters);

    if (array.includes(value)) {
      array = array.filter((item) => item !== value);
    } else {
      array.push(value);
    }

    const allSelected = array.length === plusFilters.length;

    allSelected ? setSelectedPlusFilters([]) : setSelectedPlusFilters(array);
    allSelected ? setAllPlusSelected(true) : setAllPlusSelected(false);
    setAllFilterSelected(false);
  };

  const toggleFilterPlus = (): void => {
    if (allPlusSelected) {
      setAllPlusSelected(false);
      setSelectedPlusFilters([]);
      setAllFilterSelected(false);
    } else {
      setAllPlusSelected(true);
      setSelectedPlusFilters([]);
      setAllFilterSelected(false);
    }
  };

  const toggleParapassFilter = (value: string): void => {
    let array = _.clone(selectedParapassFilters);

    if (array.includes(value)) {
      array = array.filter((item) => item !== value);
    } else {
      array.push(value);
    }

    const allSelected = array.length === parapassFilters.length;

    allSelected ? setSelectedParapassFilters([]) : setSelectedParapassFilters(array);
    allSelected ? setAllParapassSelected(true) : setAllParapassSelected(false);

    setAllFilterSelected(false);
  };

  const toggleFilterParapass = (): void => {
    if (allParapassSelected) {
      setAllParapassSelected(false);
      setSelectedParapassFilters([]);
      setAllFilterSelected(false);
    } else {
      setAllParapassSelected(true);
      setSelectedParapassFilters([]);
      setAllFilterSelected(false);
    }
  };

  const hasEvidence = (timelineReference: string): boolean => {
    let value = false;

    if (evidence.state === "hasValue" && evidence.contents) {
      value = evidence.contents.filter((item) => item.timelineReference === timelineReference).length > 0;
    }

    return value;
  };

  const hasActiveFilter = useMemo(() => {
    return (
      !allFilterSelected ||
      allParapassSelected ||
      allPlusSelected ||
      selectedPlusFilters.length > 0 ||
      selectedParapassFilters.length > 0
    );
  }, [
    allFilterSelected,
    allParapassSelected,
    allPlusSelected,
    selectedPlusFilters.length,
    selectedParapassFilters.length,
  ]);

  const hasDownloadData = useMemo(() => searchResults.length > 0, [searchResults]);
  const hasData = useMemo(() => sortedUsageData.length > 0, [sortedUsageData]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonMenuButton>
              <img
                alt="Menu"
                title="Menu"
                src={Button_Menu}
                className="menuIcon"
              />
            </IonMenuButton>
          </IonButtons>
          <HeaderTitle collapsible>{"Timeline"}</HeaderTitle>
          {hasData && (
            <IonButtons slot="end">
              <IonButton
                className="header-button"
                mode="ios"
                onClick={() => downloadTimelineData()}
                disabled={!hasDownloadData}
              >
                <img
                  alt={hasDownloadData ? "Download" : "Download Disabled"}
                  title="Download"
                  src={Button_Download_White}
                  className="headerIcon"
                />
              </IonButton>
              <IonButton
                id="filterButton"
                className="header-button"
                mode="ios"
                onClick={() => openFilter()}
              >
                <img
                  alt={hasActiveFilter ? "Filter Active" : "Filter"}
                  title={hasActiveFilter ? "Filter Active" : "Filter"}
                  src={hasActiveFilter ? Button_Filter_Active : Button_Filter}
                  className="headerIcon"
                />
              </IonButton>
              <IonButton
                className="header-button"
                mode="ios"
                onClick={() => toggleSearch()}
              >
                <img
                  alt={searchVisible ? "Search Active" : "Search"}
                  title={searchVisible ? "Search Active" : "Search"}
                  src={searchVisible ? Button_Search_Active : Button_Search}
                  className="headerIcon"
                />
              </IonButton>
            </IonButtons>
          )}
        </IonToolbar>
        {hasData && !popoverFilter && (
          <IonModal
            id="timelineModal"
            ref={modalRef}
            // trigger="filterButton"
            isOpen={filterVisible}
            initialBreakpoint={0.7}
            breakpoints={[0, 0.7, 1]}
            onDidDismiss={() => setFilterVisible(false)}
            canDismiss={true}
            className="fullscreen"
          >
            <IonHeader>
              <IonButtons slot="start">
                <IonButton
                  mode="ios"
                  className="cancelButton"
                  onClick={() => toggleFilterAll()}
                  disabled={allFilterSelected}
                >
                  {"Reset"}
                </IonButton>
              </IonButtons>
              <IonToolbar
                mode="ios"
                className="navBar"
              >
                <IonTitle>{"Filter"}</IonTitle>
                <IonButtons slot="end">
                  <IonButton
                    mode="ios"
                    className="cancelButton"
                    onClick={() => setFilterVisible(false)}
                  >
                    {"Done"}
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <IonContent className="filtersModal">
              <div>
                <IonItem
                  button
                  detail={false}
                  className="timelineFilterCheckboxContainer"
                  onClick={() => toggleFilterAll()}
                >
                  <div className="timelineCheckboxContainerInner">
                    <div className="timelineCheckboxLabel">{"All"}</div>
                    <img
                      alt=""
                      className="timelineRadioCheckbox"
                      src={allFilterSelected ? Button_Checkbox_Active : Button_Checkbox_Inactive}
                    />
                  </div>
                </IonItem>
              </div>
              {sortedPlusData.length > 0 && (
                <div>
                  <IonItem
                    button
                    detail={false}
                    className="timelineFilterCheckboxContainer"
                    onClick={() => toggleFilterPlus()}
                  >
                    <div className="timelineCheckboxContainerInner">
                      <div className="timelineCheckboxLabel">{"JRCALC"}</div>
                      <img
                        alt=""
                        className="timelineRadioCheckbox"
                        src={
                          allFilterSelected
                            ? Button_Tickbox_3_Inactive
                            : allPlusSelected
                              ? Button_Checkbox_Active
                              : Button_Checkbox_Inactive
                        }
                      />
                    </div>
                  </IonItem>
                  {plusFilters.map((item) => {
                    const selected = selectedPlusFilters.includes(item);

                    return (
                      <IonItem
                        button
                        detail={false}
                        key={item}
                        className="timelineFilterCheckboxContainer"
                        onClick={() => togglePlusFilter(item)}
                      >
                        <div className="timelineCheckboxContainerInner">
                          <div className="timelineCheckboxLabelIndent">
                            {item === "Page for Age" ? item : DataController.capitalizeFirstLetter(item) + "s"}
                          </div>
                          <img
                            alt=""
                            className="timelineRadioCheckbox"
                            src={
                              allPlusSelected || allFilterSelected
                                ? Button_Tickbox_3_Inactive
                                : selected
                                  ? Button_Checkbox_Active
                                  : Button_Checkbox_Inactive
                            }
                          />
                        </div>
                      </IonItem>
                    );
                  })}
                </div>
              )}
              {sortedParapassData.length > 0 && (
                <div>
                  <IonItem
                    button
                    detail={false}
                    className="timelineFilterCheckboxContainer"
                    onClick={() => toggleFilterParapass()}
                  >
                    <div className="timelineCheckboxContainerInner">
                      <div className="timelineCheckboxLabel">{"ParaPass"}</div>
                      <img
                        alt=""
                        className="timelineRadioCheckbox"
                        src={
                          allFilterSelected
                            ? Button_Tickbox_3_Inactive
                            : allParapassSelected
                              ? Button_Checkbox_Active
                              : Button_Checkbox_Inactive
                        }
                      />
                    </div>
                  </IonItem>
                  {parapassFilters.map((item) => {
                    const selected = selectedParapassFilters.includes(item);

                    return (
                      <IonItem
                        button
                        detail={false}
                        key={item}
                        className="timelineFilterCheckboxContainer"
                        onClick={() => toggleParapassFilter(item)}
                      >
                        <div className="timelineCheckboxContainerInner">
                          <div className="timelineCheckboxLabelIndent">{item}</div>
                          <img
                            alt=""
                            className="timelineRadioCheckbox"
                            src={
                              allParapassSelected || allFilterSelected
                                ? Button_Tickbox_3_Inactive
                                : selected
                                  ? Button_Checkbox_Active
                                  : Button_Checkbox_Inactive
                            }
                          />
                        </div>
                      </IonItem>
                    );
                  })}
                </div>
              )}
            </IonContent>
          </IonModal>
        )}
        {hasData && popoverFilter && (
          <IonPopover
            id="timelinePopover"
            className="timelinePopover"
            ref={popoverRef}
            trigger={`${window.innerWidth >= 600 ? "filterButton" : undefined}`}
            mode="ios"
            isOpen={popoverVisible}
            onDidDismiss={() => setPopoverVisible(false)}
            animated={true}
            reference="trigger"
            side="bottom"
            backdropDismiss={true}
          >
            <div>
              <div slot="fixed">
                <IonHeader>
                  <IonToolbar
                    mode="ios"
                    className="navBar"
                  >
                    <IonButtons slot="start">
                      <IonButton
                        mode="ios"
                        className="cancelButton"
                        onClick={() => toggleFilterAll()}
                        disabled={allFilterSelected}
                      >
                        {"Reset"}
                      </IonButton>
                    </IonButtons>
                    <IonTitle>{"Filter"}</IonTitle>
                    <IonButtons slot="end">
                      <IonButton
                        mode="ios"
                        className="cancelButton"
                        onClick={() => setPopoverVisible(false)}
                      >
                        {"Done"}
                      </IonButton>
                    </IonButtons>
                  </IonToolbar>
                </IonHeader>
              </div>
              <div className="filtersModal">
                <div>
                  <IonItem
                    button
                    detail={false}
                    className="timelineFilterCheckboxContainer"
                    onClick={() => toggleFilterAll()}
                  >
                    <div className="timelineCheckboxContainerInner">
                      <div className="timelineCheckboxLabel">{"All"}</div>
                      <img
                        alt=""
                        className="timelineRadioCheckbox"
                        src={allFilterSelected ? Button_Checkbox_Active : Button_Checkbox_Inactive}
                      />
                    </div>
                  </IonItem>
                </div>
                {sortedPlusData.length > 0 && (
                  <div>
                    <IonItem
                      button
                      detail={false}
                      className="timelineFilterCheckboxContainer"
                      onClick={() => toggleFilterPlus()}
                    >
                      <div className="timelineCheckboxContainerInner">
                        <div className="timelineCheckboxLabel">{"JRCALC"}</div>
                        <img
                          alt=""
                          className="timelineRadioCheckbox"
                          src={
                            allFilterSelected
                              ? Button_Tickbox_3_Inactive
                              : allPlusSelected
                                ? Button_Checkbox_Active
                                : Button_Checkbox_Inactive
                          }
                        />
                      </div>
                    </IonItem>
                    {plusFilters.map((item) => {
                      const selected = selectedPlusFilters.includes(item);

                      return (
                        <IonItem
                          button
                          detail={false}
                          key={item}
                          className="timelineFilterCheckboxContainer"
                          onClick={() => togglePlusFilter(item)}
                        >
                          <div className="timelineCheckboxContainerInner">
                            <div className="timelineCheckboxLabelIndent">
                              {item === "Page for Age" ? item : DataController.capitalizeFirstLetter(item) + "s"}
                            </div>
                            <img
                              alt=""
                              className="timelineRadioCheckbox"
                              src={
                                allPlusSelected || allFilterSelected
                                  ? Button_Tickbox_3_Inactive
                                  : selected
                                    ? Button_Checkbox_Active
                                    : Button_Checkbox_Inactive
                              }
                            />
                          </div>
                        </IonItem>
                      );
                    })}
                  </div>
                )}
                {sortedParapassData.length > 0 && (
                  <div>
                    <IonItem
                      button
                      detail={false}
                      className="timelineFilterCheckboxContainer"
                      onClick={() => toggleFilterParapass()}
                    >
                      <div className="timelineCheckboxContainerInner">
                        <div className="timelineCheckboxLabel">{"ParaPass"}</div>
                        <img
                          alt=""
                          className="timelineRadioCheckbox"
                          src={
                            allFilterSelected
                              ? Button_Tickbox_3_Inactive
                              : allParapassSelected
                                ? Button_Checkbox_Active
                                : Button_Checkbox_Inactive
                          }
                        />
                      </div>
                    </IonItem>
                    {parapassFilters.map((item) => {
                      const selected = selectedParapassFilters.includes(item);

                      return (
                        <IonItem
                          button
                          detail={false}
                          key={item}
                          className="timelineFilterCheckboxContainer"
                          onClick={() => toggleParapassFilter(item)}
                        >
                          <div className="timelineCheckboxContainerInner">
                            <div className="timelineCheckboxLabelIndent">{item}</div>
                            <img
                              alt=""
                              className="timelineRadioCheckbox"
                              src={
                                allParapassSelected || allFilterSelected
                                  ? Button_Tickbox_3_Inactive
                                  : selected
                                    ? Button_Checkbox_Active
                                    : Button_Checkbox_Inactive
                              }
                            />
                          </div>
                        </IonItem>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </IonPopover>
        )}
        {hasData && searchVisible && (
          <div className="evidenceSearchHeader">
            <div className="evidenceSearchInputContainer">
              <img
                alt=""
                src={Icon_Search_Small}
                className="evidenceSearchIcon"
              />
              <input
                value={searchText}
                className="searchInputField"
                placeholder={`Try searching “${searchPlaceholder}”`}
                onChange={searchTextChanged}
                ref={searchRef}
                onKeyDown={searchFieldButtonClicked}
              />
              {searchText.length > 0 && (
                <IonButton
                  className="searchClearButton"
                  mode="ios"
                  onClick={clearSearch}
                >
                  <img
                    alt=""
                    src={Button_Cancel}
                    className="icon-44 mt-[-5px]"
                  />
                </IonButton>
              )}
            </div>
            {searchText.length > 0 && (
              <div className="evidenceResultsText">
                {`${searchResults && searchResults.length} result${searchResults && searchResults.length !== 1 ? "s" : ""}`}
              </div>
            )}
          </div>
        )}
      </IonHeader>
      <IonContent className="page-background">
        <TimelineContainer
          id="timelineContentContainer"
          style={{
            paddingBottom: keyboardActive ? keyboardHeight : hasData ? "96px" : "40px",
          }}
        >
          {hasData ? (
            <div>
              <div className="timelineResultsContainer">
                <div className="timelineResultsInnerContainer">
                  <div className="timelineSearchResultsText">
                    {`${searchResults.length} result${searchResults && searchResults.length !== 1 ? "s" : ""}`}
                  </div>
                </div>
              </div>
              {searchResults &&
                searchResults.map((item, index) => (
                  <TimelineItem
                    key={index}
                    item={item}
                    searchText={searchText}
                    index={index}
                    hasEvidence={hasEvidence}
                    readEvidence={readEvidence}
                    addEvidence={addEvidence}
                  />
                ))}
            </div>
          ) : (
            <div className="evidenceEmptyContainer timelineEmptyContainer">
              <div className="timelineEmptyHeader">{"Reflect on your activity with Timeline"}</div>
              <img
                src={Image_Timeline_Empty}
                className="evidenceEmptyImage"
                alt=""
              />
              <div className="timelineEmptyText">
                {"Timeline activity is currently only viewable when you log in using the same account you use " +
                  "for your other Class apps (e.g. JRCALC Plus, ParaPass). If you want to reflect on your activity in other apps and add it as evidence, please log in using your associated login."}
              </div>
            </div>
          )}
        </TimelineContainer>
      </IonContent>
    </IonPage>
  );
};

export default withRouter(Timeline);
